import type {
  BarometerSuccess,
} from '@autobid/strapi-integration/typescript/auction/barometerCar'
import type { CarEnterHotbidEvent } from '@autobid/ui/types/websocket/CarEnterHotbidEvent'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import { auctionCarsQueryKey } from '@autobid/ui/composables/car/data/useAuctionCars'
import {
  getBarometerQueryKey,
} from '@autobid/ui/composables/car/data/useBarometerCar'
import { announcementCarQueryKey } from '@autobid/ui/composables/car/data/useCurrentCarAnnouncement'
import { searchCarsQueryKey } from '@autobid/ui/composables/car/data/useSearchCars'
import { BAROMETER_SOCKET_CALLBACK } from '@autobid/ui/composables/car/sockets/useBarometerSocket'
import { useUpdateCar } from '@autobid/ui/composables/car/useUpdateCar'
import { singleAuctionQueryKey } from '@autobid/ui/composables/useAuction'
import { useQueryClient } from '@tanstack/vue-query'

export function useBarometerCarEnterHotbid() {
  const { startListen } = useWebSocketStore()
  const queryClient = useQueryClient()
  const { updateBarometerCarData } = useUpdateCar()

  const carEnterHotbid = () => {
    const handleInvalidCarInAuction = (event: CarEnterHotbidEvent) => {
      const { auctionId, carId } = event.context
      const queryKey = getBarometerQueryKey({
        type: 'current',
        auctionId,
      })
      const currentData = queryClient.getQueryData(
        queryKey,
        { stale: false },
      ) as BarometerSuccess['data']['singleItem']

      if (!currentData?.item.itemData)
        return

      if (currentData.item.itemData.id === carId)
        return

      if (currentData.item.itemData.loadingNextCar)
        return

      // invalid auction state - we have other car IN_AUCTION than the WS
      queryClient.invalidateQueries(getBarometerQueryKey({ type: 'item' }))
      queryClient.invalidateQueries(getBarometerQueryKey({ type: 'current' }))
      queryClient.invalidateQueries(auctionCarsQueryKey.all())
      queryClient.invalidateQueries(announcementCarQueryKey.all())
      queryClient.invalidateQueries(searchCarsQueryKey.all())
      queryClient.invalidateQueries(singleAuctionQueryKey.all())
    }

    startListen({
      eventName: 'premium.CAR_ENTER_HOTBID',
      callbackName: BAROMETER_SOCKET_CALLBACK,
      callback: (event) => {
        const { carId, auctionId, start } = event.context
        updateBarometerCarData(auctionId, carId, (data) => {
          return {
            ...data,
            inActiveHotbidPhase: true,
            hotbidStartTime: new Date(start).toISOString(),
          }
        })
        handleInvalidCarInAuction(event)
      },
    })
  }

  return { carEnterHotbid }
}
